import { formatAddressTitleCase } from "@/utils/formatName";

// Dropoff points come from the API with the boarding-point qualifier glued to
// the city name ("CURITIBA GARAGEM", "SAO PAULO RODOVIARIA"). Displays must read
// "Curitiba (Garagem)" instead of either dropping the qualifier (when only
// city_name is used) or showing the raw glued name.

const normalizeKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

// Known qualifiers, keyed by their accent-stripped uppercase form. Multi-word
// keys are matched before single-word ones.
const QUALIFIER_LABELS: Record<string, string> = {
  "TERMINAL RODOVIARIO": "Terminal Rodoviário",
  "PONTO DE APOIO": "Ponto de Apoio",
  RODOVIARIA: "Rodoviária",
  GARAGEM: "Garagem",
  AEROPORTO: "Aeroporto",
  TERMINAL: "Terminal",
  AGENCIA: "Agência",
};

const QUALIFIER_KEYS = Object.keys(QUALIFIER_LABELS).sort(
  (a, b) => b.split(" ").length - a.split(" ").length
);

const qualifierLabel = (qualifier: string): string =>
  QUALIFIER_LABELS[normalizeKey(qualifier)] ??
  formatAddressTitleCase(qualifier);

const words = (value: string): string[] => value.split(/\s+/).filter(Boolean);

const startsWithWords = (source: string[], prefix: string[]): boolean =>
  prefix.length > 0 &&
  source.length > prefix.length &&
  prefix.every((word, i) => normalizeKey(source[i]) === normalizeKey(word));

const endsWithWords = (source: string[], suffix: string[]): boolean =>
  suffix.length > 0 &&
  source.length > suffix.length &&
  suffix.every(
    (word, i) =>
      normalizeKey(source[source.length - suffix.length + i]) ===
      normalizeKey(word)
  );

export type LocalityParts = { city: string; qualifier: string };

/**
 * Splits a dropoff point name into its city and boarding-point qualifier.
 * `cityName` is the authoritative city when the API provides it; otherwise the
 * qualifier is detected from the known keyword list.
 */
export const splitLocalityName = (
  pointName?: string | null,
  cityName?: string | null
): LocalityParts => {
  const rawName = (pointName ?? "").trim();
  const rawCity = (cityName ?? "").trim();

  if (!rawName) return { city: rawCity, qualifier: "" };
  if (!rawCity) {
    const nameWords = words(rawName);
    const matchedKey = QUALIFIER_KEYS.find((key) =>
      endsWithWords(nameWords, words(key))
    );
    if (matchedKey) {
      const keyLength = words(matchedKey).length;
      return {
        city: nameWords.slice(0, nameWords.length - keyLength).join(" "),
        qualifier: matchedKey,
      };
    }
    return { city: rawName, qualifier: "" };
  }

  const nameWords = words(rawName);
  const cityWords = words(rawCity);

  if (startsWithWords(nameWords, cityWords)) {
    return {
      city: rawCity,
      qualifier: nameWords.slice(cityWords.length).join(" "),
    };
  }
  if (endsWithWords(nameWords, cityWords)) {
    return {
      city: rawCity,
      qualifier: nameWords
        .slice(0, nameWords.length - cityWords.length)
        .join(" "),
    };
  }

  return { city: rawCity, qualifier: "" };
};

/**
 * Display-ready pieces of a locality, for renders that style the qualifier
 * differently from the city (see LocalityLabel).
 */
export const formatLocalityParts = (
  pointName?: string | null,
  cityName?: string | null
): LocalityParts => {
  const { city, qualifier } = splitLocalityName(pointName, cityName);
  return {
    city: city ? formatAddressTitleCase(city) : "",
    qualifier: qualifier ? qualifierLabel(qualifier) : "",
  };
};

/**
 * Display label for a locality: "Curitiba (Garagem)", or just "Curitiba" when
 * there is no qualifier.
 */
export const formatLocalityName = (
  pointName?: string | null,
  cityName?: string | null
): string => {
  const { city, qualifier } = formatLocalityParts(pointName, cityName);
  if (!qualifier) return city;
  if (!city) return qualifier;
  return `${city} (${qualifier})`;
};

/**
 * Same as {@link formatLocalityName} plus the state suffix: "Curitiba (Garagem) - PR".
 */
export const formatLocalityWithState = (
  pointName?: string | null,
  cityName?: string | null,
  state?: string | null
): string => {
  const label = formatLocalityName(pointName, cityName);
  const uf = (state ?? "").trim();
  if (!label) return uf;
  return uf ? `${label} - ${uf}` : label;
};
